import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organizationService } from '../../utils';

// État initial
const initialState = {
  // Organisations
  organizations: [],
  selectedOrganization: null,
  organizationProfile: null,
  organizationMembers: [],
  organizationMissions: [],
  organizationReviews: [],
  
  // Recherche et filtres
  searchResults: [],
  searchQuery: '',
  filters: {
    location: '',
    type: '',
    disciplines: [],
    verified: false,
    city: '',
    country: ''
  },
  
  // Pagination et tri
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  sortBy: 'name',
  sortOrder: 'asc',
  
  // États de chargement
  loading: false,
  searchLoading: false,
  profileLoading: false,
  membersLoading: false,
  missionsLoading: false,
  reviewsLoading: false,
  
  // Erreurs
  error: null,
  searchError: null,
  profileError: null,
  membersError: null,
  missionsError: null,
  reviewsError: null,
  
  // Statistiques
  statistics: {
    totalOrganizations: 0,
    verifiedOrganizations: 0,
    organizationsByType: {},
    organizationsByLocation: {}
  },
  
  // Organisations suivies
  followedOrganizations: [],
  followedLoading: false,
  followedError: null
};

// Actions asynchrones

// Récupérer toutes les organisations
export const fetchOrganizations = createAsyncThunk(
  'organizations/fetchOrganizations',
  async ({ page = 1, limit = 10, filters = {}, sortBy = 'name', sortOrder = 'asc' } = {}, { rejectWithValue }) => {
    try {
      const response = await organizationService.getOrganizations({
        page,
        limit,
        ...filters,
        sortBy,
        sortOrder
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des organisations');
    }
  }
);

// Récupérer une organisation par ID
export const fetchOrganizationById = createAsyncThunk(
  'organizations/fetchOrganizationById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.getOrganizationById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération de l\'organisation');
    }
  }
);

// Créer une nouvelle organisation
export const createOrganization = createAsyncThunk(
  'organizations/createOrganization',
  async (organizationData, { rejectWithValue }) => {
    try {
      const response = await organizationService.createOrganization(organizationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création de l\'organisation');
    }
  }
);

// Mettre à jour une organisation
export const updateOrganization = createAsyncThunk(
  'organizations/updateOrganization',
  async ({ id, organizationData }, { rejectWithValue }) => {
    try {
      const response = await organizationService.updateOrganization(id, organizationData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la mise à jour de l\'organisation');
    }
  }
);

// Supprimer une organisation
export const deleteOrganization = createAsyncThunk(
  'organizations/deleteOrganization',
  async (id, { rejectWithValue }) => {
    try {
      await organizationService.deleteOrganization(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression de l\'organisation');
    }
  }
);

// Télécharger un logo
export const uploadOrganizationLogo = createAsyncThunk(
  'organizations/uploadLogo',
  async ({ id, logoFile }, { rejectWithValue }) => {
    try {
      const response = await organizationService.uploadLogo(id, logoFile);
      return { id, logoUrl: response.logoUrl };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors du téléchargement du logo');
    }
  }
);

// Récupérer les membres d'une organisation
export const fetchOrganizationMembers = createAsyncThunk(
  'organizations/fetchMembers',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.getOrganizationMembers(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des membres');
    }
  }
);

// Ajouter un membre à une organisation
export const addOrganizationMember = createAsyncThunk(
  'organizations/addMember',
  async ({ id, memberData }, { rejectWithValue }) => {
    try {
      const response = await organizationService.addMember(id, memberData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'ajout du membre');
    }
  }
);

// Supprimer un membre d'une organisation
export const removeOrganizationMember = createAsyncThunk(
  'organizations/removeMember',
  async ({ id, memberId }, { rejectWithValue }) => {
    try {
      await organizationService.removeMember(id, memberId);
      return memberId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression du membre');
    }
  }
);

// Mettre à jour le rôle d'un membre
export const updateMemberRole = createAsyncThunk(
  'organizations/updateMemberRole',
  async ({ id, memberId, role }, { rejectWithValue }) => {
    try {
      const response = await organizationService.updateMemberRole(id, memberId, role);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la mise à jour du rôle');
    }
  }
);

// Récupérer les statistiques d'une organisation
export const fetchOrganizationStatistics = createAsyncThunk(
  'organizations/fetchStatistics',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.getOrganizationStatistics(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des statistiques');
    }
  }
);

// Vérifier une organisation
export const verifyOrganization = createAsyncThunk(
  'organizations/verify',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.verifyOrganization(id);
      return { id, verified: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la vérification');
    }
  }
);

// Annuler la vérification d'une organisation
export const unverifyOrganization = createAsyncThunk(
  'organizations/unverify',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.unverifyOrganization(id);
      return { id, verified: false };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'annulation de la vérification');
    }
  }
);

// Rechercher des organisations par localisation
export const searchOrganizationsByLocation = createAsyncThunk(
  'organizations/searchByLocation',
  async ({ latitude, longitude, radius = 10 }, { rejectWithValue }) => {
    try {
      const response = await organizationService.searchByLocation(latitude, longitude, radius);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la recherche par localisation');
    }
  }
);

// Rechercher des organisations par texte
export const searchOrganizations = createAsyncThunk(
  'organizations/search',
  async ({ query, filters = {}, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await organizationService.searchOrganizations(query, { ...filters, page, limit });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la recherche');
    }
  }
);

// Récupérer le profil public d'une organisation
export const fetchOrganizationProfile = createAsyncThunk(
  'organizations/fetchProfile',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.getPublicProfile(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération du profil');
    }
  }
);

// Récupérer les missions d'une organisation
export const fetchOrganizationMissions = createAsyncThunk(
  'organizations/fetchMissions',
  async ({ id, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await organizationService.getOrganizationMissions(id, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des missions');
    }
  }
);

// Récupérer les avis d'une organisation
export const fetchOrganizationReviews = createAsyncThunk(
  'organizations/fetchReviews',
  async ({ id, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await organizationService.getOrganizationReviews(id, page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des avis');
    }
  }
);

// Ajouter un avis à une organisation
export const addOrganizationReview = createAsyncThunk(
  'organizations/addReview',
  async ({ id, reviewData }, { rejectWithValue }) => {
    try {
      const response = await organizationService.addReview(id, reviewData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'ajout de l\'avis');
    }
  }
);

// Suivre une organisation
export const followOrganization = createAsyncThunk(
  'organizations/follow',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.followOrganization(id);
      return { id, followed: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors du suivi');
    }
  }
);

// Ne plus suivre une organisation
export const unfollowOrganization = createAsyncThunk(
  'organizations/unfollow',
  async (id, { rejectWithValue }) => {
    try {
      const response = await organizationService.unfollowOrganization(id);
      return { id, followed: false };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'arrêt du suivi');
    }
  }
);

// Récupérer les organisations suivies
export const fetchFollowedOrganizations = createAsyncThunk(
  'organizations/fetchFollowed',
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await organizationService.getFollowedOrganizations(page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des organisations suivies');
    }
  }
);

// Slice
const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    // Actions synchrones
    clearSelectedOrganization: (state) => {
      state.selectedOrganization = null;
      state.organizationProfile = null;
      state.organizationMembers = [];
      state.organizationMissions = [];
      state.organizationReviews = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
      state.searchError = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        location: '',
        type: '',
        disciplines: [],
        verified: false,
        city: '',
        country: ''
      };
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    clearErrors: (state) => {
      state.error = null;
      state.searchError = null;
      state.profileError = null;
      state.membersError = null;
      state.missionsError = null;
      state.reviewsError = null;
      state.followedError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch organizations
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations = action.payload.organizations || action.payload.data || [];
        state.pagination = {
          currentPage: action.payload.currentPage || 1,
          totalPages: action.payload.totalPages || 1,
          totalItems: action.payload.totalItems || 0,
          itemsPerPage: action.payload.itemsPerPage || 10
        };
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch organization by ID
      .addCase(fetchOrganizationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrganization = action.payload;
      })
      .addCase(fetchOrganizationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create organization
      .addCase(createOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations.unshift(action.payload);
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update organization
      .addCase(updateOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.organizations.findIndex(org => org.id === action.payload.id);
        if (index !== -1) {
          state.organizations[index] = action.payload;
        }
        if (state.selectedOrganization?.id === action.payload.id) {
          state.selectedOrganization = action.payload;
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete organization
      .addCase(deleteOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.organizations = state.organizations.filter(org => org.id !== action.payload);
        if (state.selectedOrganization?.id === action.payload) {
          state.selectedOrganization = null;
        }
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Upload logo
      .addCase(uploadOrganizationLogo.fulfilled, (state, action) => {
        const { id, logoUrl } = action.payload;
        const index = state.organizations.findIndex(org => org.id === id);
        if (index !== -1) {
          state.organizations[index].logo = logoUrl;
        }
        if (state.selectedOrganization?.id === id) {
          state.selectedOrganization.logo = logoUrl;
        }
      })

      // Fetch members
      .addCase(fetchOrganizationMembers.pending, (state) => {
        state.membersLoading = true;
        state.membersError = null;
      })
      .addCase(fetchOrganizationMembers.fulfilled, (state, action) => {
        state.membersLoading = false;
        state.organizationMembers = action.payload;
      })
      .addCase(fetchOrganizationMembers.rejected, (state, action) => {
        state.membersLoading = false;
        state.membersError = action.payload;
      })

      // Add member
      .addCase(addOrganizationMember.fulfilled, (state, action) => {
        state.organizationMembers.push(action.payload);
      })

      // Remove member
      .addCase(removeOrganizationMember.fulfilled, (state, action) => {
        state.organizationMembers = state.organizationMembers.filter(member => member.id !== action.payload);
      })

      // Update member role
      .addCase(updateMemberRole.fulfilled, (state, action) => {
        const index = state.organizationMembers.findIndex(member => member.id === action.payload.id);
        if (index !== -1) {
          state.organizationMembers[index] = action.payload;
        }
      })

      // Verify/Unverify organization
      .addCase(verifyOrganization.fulfilled, (state, action) => {
        const { id, verified } = action.payload;
        const index = state.organizations.findIndex(org => org.id === id);
        if (index !== -1) {
          state.organizations[index].verified = verified;
        }
        if (state.selectedOrganization?.id === id) {
          state.selectedOrganization.verified = verified;
        }
      })
      .addCase(unverifyOrganization.fulfilled, (state, action) => {
        const { id, verified } = action.payload;
        const index = state.organizations.findIndex(org => org.id === id);
        if (index !== -1) {
          state.organizations[index].verified = verified;
        }
        if (state.selectedOrganization?.id === id) {
          state.selectedOrganization.verified = verified;
        }
      })

      // Search organizations
      .addCase(searchOrganizations.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchOrganizations.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.organizations || action.payload.data || [];
        state.searchQuery = action.meta.arg.query;
      })
      .addCase(searchOrganizations.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      })

      // Search by location
      .addCase(searchOrganizationsByLocation.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchOrganizationsByLocation.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchOrganizationsByLocation.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      })

      // Fetch profile
      .addCase(fetchOrganizationProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchOrganizationProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.organizationProfile = action.payload;
      })
      .addCase(fetchOrganizationProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })

      // Fetch missions
      .addCase(fetchOrganizationMissions.pending, (state) => {
        state.missionsLoading = true;
        state.missionsError = null;
      })
      .addCase(fetchOrganizationMissions.fulfilled, (state, action) => {
        state.missionsLoading = false;
        state.organizationMissions = action.payload.missions || action.payload.data || [];
      })
      .addCase(fetchOrganizationMissions.rejected, (state, action) => {
        state.missionsLoading = false;
        state.missionsError = action.payload;
      })

      // Fetch reviews
      .addCase(fetchOrganizationReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchOrganizationReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.organizationReviews = action.payload.reviews || action.payload.data || [];
      })
      .addCase(fetchOrganizationReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      })

      // Add review
      .addCase(addOrganizationReview.fulfilled, (state, action) => {
        state.organizationReviews.unshift(action.payload);
      })

      // Follow/Unfollow organization
      .addCase(followOrganization.fulfilled, (state, action) => {
        const { id, followed } = action.payload;
        const index = state.organizations.findIndex(org => org.id === id);
        if (index !== -1) {
          state.organizations[index].followed = followed;
        }
        if (state.selectedOrganization?.id === id) {
          state.selectedOrganization.followed = followed;
        }
      })
      .addCase(unfollowOrganization.fulfilled, (state, action) => {
        const { id, followed } = action.payload;
        const index = state.organizations.findIndex(org => org.id === id);
        if (index !== -1) {
          state.organizations[index].followed = followed;
        }
        if (state.selectedOrganization?.id === id) {
          state.selectedOrganization.followed = followed;
        }
      })

      // Fetch followed organizations
      .addCase(fetchFollowedOrganizations.pending, (state) => {
        state.followedLoading = true;
        state.followedError = null;
      })
      .addCase(fetchFollowedOrganizations.fulfilled, (state, action) => {
        state.followedLoading = false;
        state.followedOrganizations = action.payload.organizations || action.payload.data || [];
      })
      .addCase(fetchFollowedOrganizations.rejected, (state, action) => {
        state.followedLoading = false;
        state.followedError = action.payload;
      });
  }
});

// Export des actions
export const {
  clearSelectedOrganization,
  clearSearchResults,
  setFilters,
  clearFilters,
  setSortBy,
  setSortOrder,
  setCurrentPage,
  clearErrors
} = organizationsSlice.actions;

// Sélecteurs
export const selectOrganizations = (state) => state.organizations.organizations;
export const selectSelectedOrganization = (state) => state.organizations.selectedOrganization;
export const selectOrganizationProfile = (state) => state.organizations.organizationProfile;
export const selectOrganizationMembers = (state) => state.organizations.organizationMembers;
export const selectOrganizationMissions = (state) => state.organizations.organizationMissions;
export const selectOrganizationReviews = (state) => state.organizations.organizationReviews;
export const selectSearchResults = (state) => state.organizations.searchResults;
export const selectFilters = (state) => state.organizations.filters;
export const selectPagination = (state) => state.organizations.pagination;
export const selectSortBy = (state) => state.organizations.sortBy;
export const selectSortOrder = (state) => state.organizations.sortOrder;
export const selectLoading = (state) => state.organizations.loading;
export const selectSearchLoading = (state) => state.organizations.searchLoading;
export const selectError = (state) => state.organizations.error;
export const selectSearchError = (state) => state.organizations.searchError;
export const selectFollowedOrganizations = (state) => state.organizations.followedOrganizations;

export default organizationsSlice.reducer;